import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ObjectInspectSidebar } from "./ObjectInspectSidebar";
import { useSidebarContext } from "../SidebarContext";
import { useObjectSettings } from "../contexts/ObjectSettingsProvider";
import { isMe, isPlayer, useHideAvatar, usePinObject, useShowAvatar } from "./object-hooks";
import notificationsCenter from "../../singleton/NotificationsCenter";
import { cloneMedia } from "../../utils/media-utils";
import { useRemoveObject } from "./object-hooks";
import { getDisplayString, useObjectList } from "./useObjectList";
import PropTypes from "prop-types";
import { LogMessageType } from "../../utils/messages";
import { Vector3 } from "three";
import { CONSTANTS } from "three-ammo";
import { getMediaType } from "../../utils/custom-utils";
import { COLLISION_LAYERS } from "../../constants";

const BODYTYPE = CONSTANTS.TYPE;

export const PROPERTIES_TABS = {
  properties: "properties",
  artworks: "artworks"
};

export const isObject3DInScene = object3D => {
  const scene = AFRAME.scenes[0];
  while (object3D) {
    if (object3D === scene.object3D) {
      return true;
    }
    object3D = object3D.parent;
  }
  return false;
};

export function ObjectInspectSidebarContainer({ hubChannel, scene }) {
  const { setSidebar } = useSidebarContext();
  const { selectedObject } = useObjectSettings();
  const { activeObject, focusedObject, focusObject, selectObject, deselectObject, unfocusObject } = useObjectList();

  const [physics, setPhysics] = useState({
    ...["enabled", "mass", "gravity", "type"].reduce((acc, prop) => {
      acc[prop] = selectedObject?.el?.components?.["body-helper"]?.data?.[prop];
      return acc;
    }, {})
  });
  const { canPin, isPinned, togglePinned } = usePinObject(hubChannel, scene, selectedObject);
  const { canRemoveObject, removeObject } = useRemoveObject(hubChannel, scene, selectedObject);
  const [visible, setVisible] = useState(selectedObject.visible || selectedObject.el.getAttribute("visible"));
  const [hid, setHid] = useState(false);
  const [currentTab, setCurrentTab] = useState(PROPERTIES_TABS.properties);
  const [shadow, setShadow] = useState({
    cast: selectedObject.el.components.shadow?.data.cast ?? false,
    receive: selectedObject.el.components.shadow?.data.receive ?? false
  });

  const onChangeTab = useCallback(tab => setCurrentTab(tab), []);

  const tabs = [
    { text: "Settings", value: PROPERTIES_TABS.properties },
    { text: "Artwork", value: PROPERTIES_TABS.artworks }
  ];

  const isAvatar = useMemo(
    () => isPlayer(focusedObject || selectedObject) || !!selectedObject.el.components["avatar-animation"],
    [selectedObject, focusedObject]
  );
  const isMyAvatar = useMemo(() => isMe(selectedObject), [selectedObject]);

  const showAvatar = useShowAvatar(hubChannel, focusedObject?.el);
  const hideAvatar = useHideAvatar(hubChannel, focusedObject?.el);

  const onToggleHideAvatar = useCallback(() => {
    if (hid) {
      showAvatar();
    } else {
      hideAvatar();
    }
    setHid(!hid);
  }, [hid, showAvatar, hideAvatar]);

  const canEditMedia =
    selectedObject && selectedObject.el.components["media-loader"]?.data?.src
      ? ["image"].includes(getMediaType(selectedObject.el.components["media-loader"].data.src)) ||
        selectedObject?.el?.components["medium-frame"] ||
        selectedObject.el.components["gltf-model-plus"]?.model?.userData?.metadata?.frame ||
        selectedObject.el.components["gltf-model-plus"]?.model?.userData?.metadata?.medium ||
        selectedObject.el.components["gltf-model-plus"]?.model?.userData?.metadata?.isArtwork
      : false;

  const hasArtwork =
    selectedObject && selectedObject.el.components["media-loader"]?.data?.src
      ? ["image", "model"].includes(getMediaType(selectedObject.el.components["media-loader"].data.src)) &&
        !(
          selectedObject.el.components["artwork"]?.data?.artworkInfo?.description ==
            "National Gallery of Art, Washington" ||
          selectedObject.el.components["artwork"]?.data?.artworkInfo?.description == "The Metropolitan Museum of Art"
        )
      : false;

  const isArtwork = selectedObject.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.isArtwork;
  const [hasMediumOrFrame, setHasMediumOrFrame] = useState(false);
  useEffect(() => {
    const checkMediumOrFrame =
      selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.frame ||
      selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.medium ||
      selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.isArtwork;
    setHasMediumOrFrame(!!checkMediumOrFrame);
  }, [selectedObject]);

  useEffect(() => {
    selectedObject?.el?.addEventListener("model-loaded", () => {
      const checkMediumOrFrame =
        selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.frame ||
        selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.medium ||
        selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.isArtwork;
      setHasMediumOrFrame(!!checkMediumOrFrame);
    });

    return () => {
      selectedObject?.el?.removeEventListener("model-loaded", () => {
        const checkMediumOrFrame =
          selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.frame ||
          selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.medium ||
          selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.isArtwork;
        setHasMediumOrFrame(!!checkMediumOrFrame);
      });
    };
  }, [selectedObject]);

  useEffect(() => {
    if (selectedObject.el.components["body-helper"]) {
      setPhysics(selectedObject.el.components["body-helper"].data);
    }
  }, [selectedObject, focusedObject]);

  const itemName = getDisplayString(selectedObject.el);

  const onClone = useCallback(() => {
    const el = selectedObject.el;
    const { entity } = cloneMedia(el, "#interactable-media", el.components["media-loader"].data.src);

    entity.object3D.scale.copy(el.object3D.scale);
    entity.object3D.matrixNeedsUpdate = true;

    entity.setAttribute("offset-relative-to", {
      target: "#avatar-pov-node",
      offset: { x: 0, y: 0, z: -1.5 * el.object3D.scale.z }
    });

    notificationsCenter.success(LogMessageType.objectCloned);
  }, [selectedObject]);

  const onDelete = useCallback(() => {
    if (!canRemoveObject) return;

    removeObject();
    setSidebar(null);
    notificationsCenter.success(LogMessageType.objectDeleted);
  }, [removeObject, setSidebar, canRemoveObject]);

  const onToggleVisible = useCallback(() => {
    const newVisible = !selectedObject.el.object3D.visible;
    // selectedObject.el.setAttribute("media-loader", { visible: newVisible });
    selectedObject.el.setAttribute("visible", newVisible);
    setVisible(newVisible);
  }, [selectedObject]);

  const onToggleReceiveShadow = useCallback(() => {
    const receive = selectedObject?.el?.components?.shadow?.data?.receive;
    const data = { receive: !receive };
    selectedObject.el.setAttribute("shadow", data);
    setShadow({
      ...shadow,
      receive: !receive
    });
  }, [selectedObject, shadow]);

  const onToggleCastShadow = useCallback(() => {
    const cast = selectedObject?.el?.components?.shadow?.data?.cast;
    const data = { cast: !cast };
    selectedObject.el.setAttribute("shadow", data);
    // selectedObject.el.setAttribute("media-loader", { shadow: { cast: !cast, receive: shadow.receive } });
    setShadow({
      ...shadow,
      cast: !cast
    });
  }, [selectedObject, shadow]);

  const onTogglePhysics = useCallback(() => {
    const enabled = physics.enabled;
    const newEnabled = !enabled;
    selectedObject.el.setAttribute("body-helper", { enabled: newEnabled });

    if (!newEnabled) {
      selectedObject.el.setAttribute("body-helper", { type: "kinematic" });
    }

    setPhysics({
      ...selectedObject?.el?.components?.["body-helper"]?.data,
      enabled: newEnabled,
      type: !newEnabled ? "kinematic" : physics.type
    });
  }, [selectedObject, physics]);

  const onChangeMass = useCallback(
    mass => {
      setPhysics({ ...selectedObject?.el?.components?.["body-helper"]?.data, mass });
      selectedObject.el.setAttribute("body-helper", { mass });
    },
    [selectedObject]
  );

  const onChangeGravity = useCallback(
    gravity => {
      const data = { x: 0, y: gravity, z: 0 };
      setPhysics({ ...selectedObject?.el?.components?.["body-helper"]?.data, gravity: data });
      selectedObject.el.setAttribute("body-helper", { gravity: data });
    },
    [selectedObject]
  );

  const onChangeBodyType = useCallback(
    type => {
      setPhysics({ ...selectedObject?.el?.components?.["body-helper"]?.data, type });
      if (type === "dynamic") {
        selectedObject.el.setAttribute("body-helper", {
          type,
          angularDamping: 0.01,
          linearDamping: 0.01,
          // linearSleepingThreshold: 1.6,
          // angularSleepingThreshold: 2.5,
          collisionFilterMask: COLLISION_LAYERS.DEFAULT_INTERACTABLE
        });
      } else {
        selectedObject.el.setAttribute("body-helper", { type });
      }
    },
    [selectedObject]
  );

  const [transform, setTransform] = useState({
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 }
  });

  useEffect(() => {
    if (!selectedObject) return;

    const pos = selectedObject.el.getAttribute("position") || { x: 0, y: 0, z: 0 };
    const rot = selectedObject.el.getAttribute("rotation") || { x: 0, y: 0, z: 0 };
    const scl = selectedObject.el.getAttribute("scale") || { x: 1, y: 1, z: 1 };

    setTransform({ position: pos, rotation: rot, scale: scl });
  }, [selectedObject]);

  const transformObject = useCallback(
    async ({ position, rotation, scale }) => {
      if (!selectedObject) return;

      const networkedEntity = await NAF.utils.getNetworkedEntity(selectedObject.el);
      NAF.utils.takeOwnership(networkedEntity);

      selectedObject.el.setAttribute("position", new Vector3(position.x, position.y, position.z));
      selectedObject.el.setAttribute("rotation", new Vector3(rotation.x, rotation.y, rotation.z));
      selectedObject.el.setAttribute("scale", new Vector3(scale.x, scale.y, scale.z));

      setTransform({ position, rotation, scale });
    },
    [selectedObject]
  );

  const canTransform = hubChannel.can("spawn_and_move_media") && !isPinned && !isAvatar && !isMyAvatar;
  const canClone = hubChannel.can("spawn_and_move_media") && !isAvatar && !isMyAvatar;
  const canEdit = hubChannel.can("spawn_and_move_media") && !isAvatar && !isMyAvatar;

  const onDownload = useCallback(async () => {
    if (!selectedObject) return;

    const url = selectedObject.el.components["media-loader"].data.src;
    const fileName = url.split("/").pop();

    try {
      // Fetch the file as a blob
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const blob = await response.blob();

      // Create a temporary object URL
      const blobUrl = URL.createObjectURL(blob);

      // Create a link and trigger the download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the object URL to free up memory
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  }, [selectedObject]);

  const onCopy = useCallback(() => {
    if (!selectedObject) return;

    navigator.clipboard.writeText(selectedObject.el.components["media-loader"].data.src);
    notificationsCenter.success(LogMessageType.copied);
  }, [selectedObject]);

  const handleFocusObject = useCallback(() => {
    selectObject(selectedObject);
    focusObject(selectedObject);
    setSidebar("null");
  }, [selectedObject, focusObject, selectObject, setSidebar]);

  useEffect(() => {
    if (!selectedObject || !canEdit) return;

    selectedObject.el.setAttribute("transform-listener", "");

    return () => {
      selectedObject.el.removeAttribute("transform-listener");
    };
  }, [selectedObject, canEdit]);

  useEffect(() => {
    if (!selectedObject) return;

    const handleTransformChanged = event => {
      const { type } = event.detail;

      if (type === "visible") {
        setVisible(event.detail.value);
      }

      const obj = selectedObject.el.object3D;
      setTransform({
        position: { x: obj.position.x, y: obj.position.y, z: obj.position.z },
        rotation: {
          x: THREE.MathUtils.radToDeg(obj.rotation.x),
          y: THREE.MathUtils.radToDeg(obj.rotation.y),
          z: THREE.MathUtils.radToDeg(obj.rotation.z)
        },
        scale: { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z }
      });
    };

    selectedObject.el.addEventListener("transformchanged", handleTransformChanged);

    return () => {
      if (selectedObject?.el) {
        selectedObject.el.removeEventListener("transformchanged", handleTransformChanged);
      }
    };
  }, [selectedObject]);

  const handleClose = useCallback(() => {
    setSidebar(null);
    if (!focusedObject) {
      deselectObject();
    }
  }, [setSidebar, focusedObject, deselectObject]);

  useEffect(() => {
    if (!focusedObject && isAvatar) {
      handleFocusObject();
    }
  }, [isAvatar, focusedObject, handleFocusObject]);

  // Listen every second if the object is deleted
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isObject3DInScene(selectedObject.el.object3D)) {
        setSidebar(null);
        notificationsCenter.info(LogMessageType.objectFocusedDeleted);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedObject, setSidebar]);

  useEffect(() => {
    if (!selectedObject) {
      setSidebar(null);
    }
  }, [selectedObject, setSidebar]);

  if (isAvatar || isMyAvatar) {
    return <></>;
  }

  return (
    <>
      <ObjectInspectSidebar
        item={selectedObject}
        name={itemName}
        onToggleVisible={onToggleVisible}
        onToggleReceiveShadow={onToggleReceiveShadow}
        onToggleCastShadow={onToggleCastShadow}
        isPinned={isPinned}
        onTogglePersistency={() => canPin && togglePinned()}
        onTogglePhysics={onTogglePhysics}
        onChangeMass={onChangeMass}
        onChangeGravity={onChangeGravity}
        onChangeBodyType={() => {}}
        physics={physics}
        onClose={handleClose}
        onClone={onClone}
        canDelete={canRemoveObject}
        visible={visible}
        onDelete={onDelete}
        onTransform={transformObject}
        transform={transform}
        canTransform={canTransform}
        canClone={canClone}
        canEdit={canEdit}
        canPin={canPin}
        BODYTYPE={BODYTYPE}
        onDownload={onDownload}
        onCopy={onCopy}
        focusedObject={focusedObject}
        onFocusObject={handleFocusObject}
        currentTab={currentTab}
        tabs={tabs}
        onChangeTab={onChangeTab}
        isAvatar={isAvatar}
        isMe={isMyAvatar}
        hid={hid}
        onToggleHideAvatar={onToggleHideAvatar}
        hasArtwork={hasArtwork}
        isArtwork={isArtwork}
        propertiesTabs={PROPERTIES_TABS}
        shadow={shadow}
        canEditMedia={canEditMedia}
        hasMediumOrFrame={hasMediumOrFrame}
      />
    </>
  );
}

ObjectInspectSidebarContainer.propTypes = {
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired
};
